import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  IconButton,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { jobApi } from '../services/api.service';

const JobSiteManager = ({ jobId, sites: initialSites, equipmentPlans = [], onSitesUpdate, warehouses = [], racks = [], bays = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [sites, setSites] = useState(initialSites || []);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteToDelete, setSiteToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    contact_name: '',
    contact_phone: '',
    notes: '',
    description: '',
    stackWarehouseId: null,
    stackRackId: null,
    stackBayId: null
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialSites) {
      setSites(initialSites);
    }
  }, [initialSites]);

  const fetchSites = async () => {
    try {
      setLoading(true);
      const response = await jobApi.getJobSites(jobId);
      setSites(response.data || []);
      if (onSitesUpdate) {
        onSitesUpdate(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      enqueueSnackbar('Failed to load sites', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (site = null) => {
    if (site) {
      setSelectedSite(site);
      setFormData({
        name: site.name || '',
        address: site.address || '',
        city: site.city || '',
        state: site.state || '',
        zip: site.zip || '',
        contact_name: site.contact_name || '',
        contact_phone: site.contact_phone || '',
        notes: site.notes || '',
        description: site.description || '',
        stackWarehouseId: site.stackWarehouseId || null,
        stackRackId: site.stackRackId || null,
        stackBayId: site.stackBayId || null
      });
    } else {
      setSelectedSite(null);
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        contact_name: '',
        contact_phone: '',
        notes: '',
        description: '',
        stackWarehouseId: null,
        stackRackId: null,
        stackBayId: null
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSite(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      contact_name: '',
      contact_phone: '',
      notes: '',
      description: '',
      stackWarehouseId: null,
      stackRackId: null,
      stackBayId: null
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) {
      errors.name = 'Site name is required';
    }
    // Address, city, and state are now optional
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const siteData = {
        ...formData,
        job_id: jobId
      };

      if (selectedSite) {
        // Update existing site
        await jobApi.updateJobSite(jobId, selectedSite.id, siteData);
        enqueueSnackbar('Site updated successfully', { variant: 'success' });
      } else {
        // Create new site for this job
        await jobApi.createJobSite(jobId, siteData);
        enqueueSnackbar('Site created successfully', { variant: 'success' });
      }

      await fetchSites();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving site:', error);
      enqueueSnackbar('Failed to save site', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (site) => {
    setSiteToDelete(site);
    setDeleteDialogOpen(true);
  };

  const handleViewSiteDetails = (site) => {
    navigate(`/jobs/${jobId}/sites/${site.id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!siteToDelete) return;

    try {
      setLoading(true);
      await jobApi.deleteJobSite(jobId, siteToDelete.id);
      enqueueSnackbar('Site deleted successfully', { variant: 'success' });
      await fetchSites();
    } catch (error) {
      console.error('Error deleting site:', error);
      enqueueSnackbar('Failed to delete site', { variant: 'error' });
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setSiteToDelete(null);
    }
  };

  const handleFieldChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const getEquipmentCount = (site) => {
    if (!site || !equipmentPlans) return 0;
    
    // Count equipment planned for this specific site
    const siteEquipmentCount = equipmentPlans
      .filter(plan => plan.site_id === site.id)
      .reduce((total, plan) => total + (plan.needed_quantity || 0), 0);
    
    return siteEquipmentCount;
  };

  return (
    <Box>
      <Box sx={{ 
        mb: 3, 
        p: 3,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap', 
        gap: 2,
        position: 'relative',
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '5px',
          background: 'linear-gradient(135deg, #2C2C2C, #4A4A4A)',
          borderRadius: '4px 4px 0 0',
        }
      }}>
        <Typography variant="h6">
          Job Sites ({sites.length})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Site
        </Button>
      </Box>

      {sites.length === 0 ? (
        <Alert severity="info">
          No sites added to this job yet. Click "Add Site" to create one.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {sites.map((site) => (
            <Grid item xs={12} sm={6} md={4} key={site.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main'
                  }
                }}
                onClick={() => handleViewSiteDetails(site)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon color="primary" fontSize="small" />
                    {site.name}
                  </Typography>
                  
                  {site.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {site.description}
                    </Typography>
                  )}

                  <Stack spacing={1}>
                    {site.address && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <LocationIcon fontSize="small" sx={{ mt: 0.5, flexShrink: 0 }} />
                        <Box>{site.address}</Box>
                      </Typography>
                    )}

                    {site.contact_name && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" />
                        {site.contact_name}
                      </Typography>
                    )}

                    {site.contact_phone && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" />
                        {site.contact_phone}
                      </Typography>
                    )}

                    {site.notes && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <DescriptionIcon fontSize="small" sx={{ mt: 0.5, flexShrink: 0 }} />
                        <Box sx={{ wordBreak: 'break-word' }}>{site.notes}</Box>
                      </Typography>
                    )}
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <InventoryIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {getEquipmentCount(site)} equipment items
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Stack: {(() => {
                      // Check if site has stack location data
                      if (!site.stackWarehouseId && !site.stackRackId && !site.stackBayId) {
                        return 'Not assigned';
                      }
                      if (!Array.isArray(warehouses) || !Array.isArray(racks) || !Array.isArray(bays)) {
                        return 'Loading...';
                      }
                      const wh = warehouses.find(w => w.id === site.stackWarehouseId);
                      const r = racks.find(r => r.id === site.stackRackId);
                      const b = bays.find(b => b.id === site.stackBayId);
                      const parts = [wh?.name, r?.name, b?.name].filter(Boolean);
                      return parts.length > 0 ? parts.join('-') : 'Not assigned';
                    })()}
                  </Typography>
                </CardContent>

                <CardActions>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDialog(site);
                    }}
                    title="Edit site"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(site);
                    }}
                    title="Delete site"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Site Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedSite ? 'Edit Site' : 'Add New Site'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Site Name"
              value={formData.name}
              onChange={handleFieldChange('name')}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleFieldChange('description')}
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={handleFieldChange('address')}
              error={!!formErrors.address}
              helperText={formErrors.address}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={handleFieldChange('city')}
                  error={!!formErrors.city}
                  helperText={formErrors.city}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={handleFieldChange('state')}
                  error={!!formErrors.state}
                  helperText={formErrors.state}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP"
                  value={formData.zip}
                  onChange={handleFieldChange('zip')}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Contact Name"
              value={formData.contact_name}
              onChange={handleFieldChange('contact_name')}
            />

            <TextField
              fullWidth
              label="Contact Phone"
              value={formData.contact_phone}
              onChange={handleFieldChange('contact_phone')}
            />

            <Divider sx={{ my: 1 }}>
              <Chip label="Stack Location (Optional)" size="small" />
            </Divider>

            <Autocomplete
              options={warehouses}
              getOptionLabel={(option) => option.name || ''}
              value={warehouses.find(w => w.id === formData.stackWarehouseId) || null}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  stackWarehouseId: newValue?.id || null,
                  stackRackId: null, // Reset dependent fields
                  stackBayId: null
                });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Stack Warehouse" helperText="Where this site's equipment is staged" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />

            <Autocomplete
              options={racks}
              getOptionLabel={(option) => option.name || ''}
              value={racks.find(r => r.id === formData.stackRackId) || null}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  stackRackId: newValue?.id || null,
                  stackBayId: null // Reset dependent field
                });
              }}
              disabled={!formData.stackWarehouseId}
              renderInput={(params) => (
                <TextField {...params} label="Stack Rack" helperText="Rack within the warehouse" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />

            <Autocomplete
              options={bays}
              getOptionLabel={(option) => option.name || ''}
              value={bays.find(b => b.id === formData.stackBayId) || null}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  stackBayId: newValue?.id || null
                });
              }}
              disabled={!formData.stackRackId}
              renderInput={(params) => (
                <TextField {...params} label="Stack Bay" helperText="Bay within the rack" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={handleFieldChange('notes')}
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {selectedSite ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the site "{siteToDelete?.name}"?
          </Typography>
          {siteToDelete && getEquipmentCount(siteToDelete) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This site has {getEquipmentCount(siteToDelete)} equipment items assigned to it.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobSiteManager;